import React from "react";

import {
    Svg as LabUISvg,
    Props,
    SvgElement,
    svgNames as labuiSvgNames,
} from "@thelabnyc/thelabui/src/components/Svg";

/**
 * thelab-ui provides some icons that might be useful, but you'll
 * definitely need project-specific icons. Put the names of those
 * icons here.
 */
const SvgElementNames = [
    "search",
    "account",
    "location",
    "mail",
    "phone",
    "link",
    "burger",
    "close",
    "plus",
    "minus",
    "caret",
    "arrow",
    "download",
    "check",
    "review good",
    "review bad",
    "video/play",
    "video/pause",
    "audio-on",
    "audio-off",
    "clock",
    "star",
    "logo-monogram",
    // If we live in a future where one can import this:
    // import { constants } from "@reactivated/constants";
    // and then uncomment the server.cms.blocks.SvgElement.ClientSvgs line below, then do it.
    //
    // When adding a new client logo, add it to that 👇 python file so it can be chosen in the cms
    // ...Object.values(constants["server.cms.blocks.SvgElement.ClientSvgs"]),
] as const;

/** If you wish to exclude thelab-ui icons entirely, add their names here. */
const labuiBlacklist = [""];
const labuiSvgNamesFiltered = labuiSvgNames.filter(
    (name) => !labuiBlacklist.includes(name),
);

/**
 * Project-specific SVGs plus SVGs from thelab-ui.
 *
 * This deduplicates, so if you put something into SvgElementNames that's in
 * labuiSvgNames, it'll only show up once. This is useful if you want
 * to override something from thelab-ui.
 */
export const allSvgNames = [
    ...new Set([...labuiSvgNamesFiltered, ...SvgElementNames].sort()),
] as const;

export type SVGName = (typeof allSvgNames)[number];

export const logoMonogramPathD =
    "M 43.498047 0 C 18.513147 0 -4.7369516e-15 20.4101 0 45.5 C 0 70.5899 18.513147 91 43.498047 91 C 68.483047 91 87.001953 70.5899 87.001953 45.5 C 87.001953 32.3977 81.948347 20.569766 73.560547 12.259766 L 73.560547 12.263672 L 73.558594 12.261719 C 72.627894 13.592619 71.358716 14.691747 69.791016 15.498047 C 69.698316 15.545347 69.611884 15.581953 69.521484 15.626953 C 76.088584 23.094753 79.613281 33.678506 79.613281 45.503906 C 79.613281 68.375506 66.440847 86.800781 43.498047 86.800781 C 20.555347 86.800781 7.3828125 68.3716 7.3828125 45.5 C 7.3828125 22.6284 20.555347 4.3867187 43.498047 4.3867188 C 46.836947 4.3867188 49.968366 4.7739494 52.884766 5.5058594 C 53.454066 4.0960894 54.226631 2.769455 55.207031 1.578125 C 51.513531 0.551202 47.598247 1.4802974e-16 43.498047 0 z M 64.558594 0.59960938 C 63.824323 0.60131176 63.066953 0.71016837 62.306641 0.93945312 C 57.124441 2.5023651 55.071188 7.9359831 54.992188 12.626953 C 54.980888 13.221453 55.74585 13.493866 56.09375 13.009766 L 56.09375 13.005859 C 56.66975 12.215359 57.489428 11.44275 58.548828 11.34375 C 59.902028 11.21085 60.411169 11.602838 61.355469 12.210938 C 62.561769 12.990137 63.939803 13.869056 66.533203 13.222656 C 68.279403 12.790356 69.657125 11.792097 70.515625 10.341797 C 71.367225 8.9185169 71.619244 7.1570581 71.214844 5.5175781 C 70.493506 2.59543 67.740433 0.59223236 64.558594 0.59960938 z M 43.265625 11.703125 C 32.225725 11.703125 24.212891 18.747419 24.212891 28.449219 C 24.212891 32.599719 25.694334 36.402725 28.427734 39.328125 C 25.890834 41.519325 23.642578 45.117938 23.642578 49.835938 C 23.642578 53.220737 24.849119 56.140994 27.074219 58.183594 C 21.713519 60.595494 20.5625 63.892616 20.5625 66.291016 L 20.554688 66.291016 L 20.554688 66.292969 C 20.554688 72.722469 28.341588 76.416016 41.929688 76.416016 C 59.604388 76.416016 70.162109 69.927141 70.162109 59.056641 C 70.162109 51.061941 64.525844 46.087891 55.464844 46.087891 C 51.120744 46.087891 46.788403 47.192869 42.970703 48.167969 C 40.090403 48.904469 37.609966 49.535156 35.759766 49.535156 C 33.236466 49.535156 31.949219 48.388966 31.949219 46.134766 C 31.949219 44.787966 32.396937 43.661925 33.273438 42.765625 L 33.941406 43.205078 C 36.550606 44.407678 39.756066 45.033203 43.259766 45.033203 C 54.478166 45.033203 62.3125 38.217925 62.3125 28.453125 C 62.3125 24.764325 61.125594 21.290975 58.871094 18.421875 C 57.432094 16.771075 55.703997 15.388403 53.779297 14.345703 L 52.876953 13.855469 C 50.229453 12.713669 46.543525 11.703125 43.265625 11.703125 z M 43.085938 17.53125 L 43.085938 17.535156 C 46.277938 17.535156 48.582031 21.953916 48.582031 28.791016 C 48.582031 35.220616 46.603169 39.214844 43.417969 39.214844 C 40.232669 39.214844 37.921875 34.863 37.921875 28.125 L 37.916016 28.125 C 37.916016 21.5918 39.893937 17.53125 43.085938 17.53125 z M 49.033203 59.541016 C 52.873503 59.541016 54.816406 60.946203 54.816406 63.720703 L 54.822266 63.720703 C 54.822266 67.909403 50.576941 70.214844 42.869141 70.214844 L 42.863281 70.214844 C 37.803081 70.214844 31.769531 69.162131 31.769531 64.144531 C 31.769531 62.957731 32.11185 61.766887 32.65625 60.992188 L 32.65625 60.988281 L 32.658203 60.988281 C 33.534703 61.172981 34.476738 61.255859 35.585938 61.255859 C 37.829138 61.255859 40.170687 60.816472 42.429688 60.388672 C 44.634487 59.969772 46.914203 59.541016 49.033203 59.541016 z ";

/**
 * This repo's solution for using icon-like SVGs. "Icon-like" means that the SVG
 * is a relatively small and simple graphic. Most, if not all of our icons are
 * monochromatic, their fills are modified with CSS, and they might be reused in
 * different contexts around the site.
 *
 * This is building around a lab-ui base Svg component, which means that some of
 * these icons are not specific to this repository and might not be what we want
 * to use. They can be overridden if necessary.
 *
 * To add an SVG:
 * - Add a name to `SvgElementNames`
 * - Make the SVG TSX-friendly. https://react-SvgElementr.com/playground/?typescript=true
 *   can help with this
 * - Add a case with your name and your element, replacing `<SvgElement>` with `<SvgElement>`.
 *   You should only need `{...props}` and the `viewBox` attribute; height/width and
 *   fill color can be set with CSS
 *
 * You can hardcode fill colors, but you'll need to be very sure that the color will
 * not change. Typically the logo is a place where you'd do this.
 *
 * Some images might be in the SVG format but not fit this mold, and can be treated as
 * static images instead (placed in the static folder and loaded with `<img>`).
 */
export const Svg = ({ name, ...props }: Props<SVGName>) => {
    switch (name) {
        case "search":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M11.26 3.3a7.97 7.97 0 0 0-5.63 13.58 7.98 7.98 0 0 0 10.5.66l3.77 3.76a1 1 0 0 0 1.41 0 1 1 0 0 0 0-1.41l-3.76-3.77a7.98 7.98 0 0 0-6.3-12.82zm0 2a5.94 5.94 0 0 1 4.21 10.18 5.94 5.94 0 0 1-8.43-.01 5.94 5.94 0 0 1 4.22-10.18z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "account":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M12.24 1a5.31 5.31 0 0 0-5.32 5.27 5.31 5.31 0 0 0 10.65 0c0-2.9-2.4-5.27-5.33-5.27zm0 2a3.28 3.28 0 0 1 3.33 3.27 3.28 3.28 0 0 1-3.33 3.27 3.28 3.28 0 0 1-3.32-3.27A3.28 3.28 0 0 1 12.24 3zm.01 8.81a9.43 9.43 0 0 0-9.21 7.8 2.33 2.33 0 0 0 2.25 2.74h13.92c1.43 0 2.5-1.35 2.25-2.74a9.43 9.43 0 0 0-9.21-7.8zm0 2c3.6 0 6.6 2.64 7.24 6.15.05.24-.09.4-.28.4H5.29c-.19 0-.33-.16-.28-.4a7.41 7.41 0 0 1 7.24-6.15z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "location":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M12.24 1a8.58 8.58 0 0 0-7.38 3.77v.02l-.16.23v.01a8.07 8.07 0 0 0 .14 8.67v.01l.04.05v.01l.03.03.07.1.07.1L12 23.7l6.64-9.27h-.01l.3-.4V14h.01l.14-.19.02-.03.04-.06a8.14 8.14 0 0 0-2.52-11.4A8.62 8.62 0 0 0 12.24 1zm-.05 2a6.7 6.7 0 0 1 3.36 1.02 6.08 6.08 0 0 1 1.98 8.49l-.24.37L12 20.27l-5.3-7.4-.01-.02-.08-.1-.06-.1-.04-.05a6.03 6.03 0 0 1-.1-6.52l.12-.2A6.58 6.58 0 0 1 12.19 3zM12 5.82a3.5 3.5 0 0 0-3.54 3.44A3.5 3.5 0 0 0 12 12.7a3.5 3.5 0 0 0 3.54-3.44A3.5 3.5 0 0 0 12 5.82zm0 2c.91 0 1.54.68 1.54 1.44 0 .76-.63 1.44-1.54 1.44-.9 0-1.54-.68-1.54-1.44 0-.76.63-1.44 1.54-1.44z"
                        fill="currentColor"
                    />
                </SvgElement>
            );

        case "mail":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M2 4C.9 4 0 4.9 0 6v12c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H2zm1.62 2h16.8L12 12.72 3.62 6zM2 7.26l9.38 7.52a1 1 0 0 0 1.24 0L22 7.3V18H2V7.26z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "phone":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M7 0C5.9 0 5 .9 5 2v20c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V2c0-1.1-.9-2-2-2H7zm0 2h10v20H7V2zm3 1a1 1 0 0 0-1 1 1 1 0 0 0 1 1h4a1 1 0 0 0 1-1 1 1 0 0 0-1-1h-4zm2 16a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "link":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M16.9.83c-1.57 0-3.15.6-4.34 1.8l-2.61 2.6a1 1 0 0 0 0 1.42 1 1 0 0 0 1.4 0l2.63-2.61a4.12 4.12 0 1 1 5.84 5.83L17.2 12.5a1 1 0 0 0 0 1.42 1 1 0 0 0 1.42 0l2.61-2.62A6.15 6.15 0 0 0 16.9.83zm-1.2 6.33a1 1 0 0 0-.71.29l-7.54 7.54a1 1 0 0 0 0 1.41 1 1 0 0 0 1.41 0l7.54-7.54a1 1 0 0 0 0-1.41 1 1 0 0 0-.7-.3zm-9.76 2.5a1 1 0 0 0-.7.29l-2.62 2.61a6.15 6.15 0 0 0 0 8.67 6.15 6.15 0 0 0 8.67 0l2.62-2.61a1 1 0 0 0 0-1.42 1 1 0 0 0-1.42 0l-2.62 2.62a4.12 4.12 0 1 1-5.83-5.84l2.61-2.62a1 1 0 0 0 0-1.41 1 1 0 0 0-.7-.3z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "burger":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M4 5a1 1 0 0 0-1 1 1 1 0 0 0 1 1h16a1 1 0 0 0 1-1 1 1 0 0 0-1-1H4zm0 6a1 1 0 0 0-1 1 1 1 0 0 0 1 1h16a1 1 0 0 0 1-1 1 1 0 0 0-1-1H4zm0 6a1 1 0 0 0-1 1 1 1 0 0 0 1 1h16a1 1 0 0 0 1-1 1 1 0 0 0-1-1H4z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "close":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M4 3a1 1 0 0 0-.7.3 1 1 0 0 0 0 1.4l7.29 7.3-7.3 7.3a1 1 0 0 0 0 1.4 1 1 0 0 0 1.42 0L12 13.42l7.3 7.3a1 1 0 0 0 1.4 0 1 1 0 0 0 0-1.42L13.42 12l7.3-7.3a1 1 0 0 0 0-1.4 1 1 0 0 0-1.42 0L12 10.58l-7.3-7.3A1 1 0 0 0 4 3z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "plus":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M11.9 1.1a1 1 0 0 0-1 1V11H2a1 1 0 0 0-1 1 1 1 0 0 0 1 1h8.9v8.9a1 1 0 0 0 1 1 1 1 0 0 0 1-1V13h8.9a1 1 0 0 0 1-1 1 1 0 0 0-1-1h-8.9V2.1a1 1 0 0 0-1-1z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "minus":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M2 11a1 1 0 0 0-1 1 1 1 0 0 0 1 1h19.8a1 1 0 0 0 1-1 1 1 0 0 0-1-1z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "caret":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M8 3a1 1 0 0 0-.7.3 1 1 0 0 0 0 1.4l7.29 7.3-7.3 7.3a1 1 0 0 0 0 1.4 1 1 0 0 0 1.42 0l8-8a1 1 0 0 0 0-1.4l-8-8A1 1 0 0 0 8 3Z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "arrow":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M14 4a1 1 0 0 0-.7.3 1 1 0 0 0 0 1.4l5.29 5.3H4a1 1 0 0 0-1 1 1 1 0 0 0 1 1h14.59l-5.3 5.3a1 1 0 0 0 0 1.4 1 1 0 0 0 1.42 0l7-7a1 1 0 0 0 0-1.4l-7-7A1 1 0 0 0 14 4z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "download":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M12 1a1 1 0 0 0-1 1v14.59l-5.3-5.3A1 1 0 0 0 5 11a1 1 0 0 0-.7.3 1 1 0 0 0 0 1.4l7 7a1 1 0 0 0 1.4 0l7-7a1 1 0 0 0 0-1.4 1 1 0 0 0-1.4 0L13 16.58V2a1 1 0 0 0-1-1zM4 22a1 1 0 0 0-1 1 1 1 0 0 0 1 1h16a1 1 0 0 0 1-1 1 1 0 0 0-1-1H4z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "check":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M20.52 6.36a1 1 0 0 0-.7.3L9.13 17.33l-4.95-4.96a1 1 0 0 0-1.41 0 1 1 0 0 0 0 1.42l5.66 5.66a1 1 0 0 0 1.41 0l11.39-11.4a1 1 0 0 0 0-1.4 1 1 0 0 0-.7-.3z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "review good":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M11.35 3a1 1 0 0 0-.91.59l-2.97 6.6H5.62A2.63 2.63 0 0 0 3 12.8v5.6A2.6 2.6 0 0 0 5.62 21h2.43a1 1 0 0 0 .03 0 1 1 0 0 0 .03 0h9.12a2.63 2.63 0 0 0 2.62-2.2l1.12-7.2a2.58 2.58 0 0 0-1.52-2.77 2.64 2.64 0 0 0-1.1-.23h-3.57V6.4a3.38 3.38 0 0 0-2.12-3.14 3.46 3.46 0 0 0-1.3-.26zm.59 2.13c.16.07.3.16.43.28a1.4 1.4 0 0 1 .4.99v3.2a1 1 0 0 0 1 1h4.6a.64.64 0 0 1 .48.21.6.6 0 0 1 .14.48l-1.11 7.2a.6.6 0 0 1-.21.36.63.63 0 0 1-.41.15 1 1 0 0 0-.02 0H9.11v-7.59l2.83-6.28zM5.62 12.2h1.43V19H5.62a.63.63 0 0 1-.44-.18.59.59 0 0 1-.18-.42v-5.6c0-.16.06-.3.18-.42a.63.63 0 0 1 .44-.18z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "review bad":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M6.77 3a2.63 2.63 0 0 0-2.62 2.2l-1.12 7.2a2.58 2.58 0 0 0 1.52 2.77c.35.16.72.23 1.1.23h3.57v2.2a3.37 3.37 0 0 0 2.12 3.14c.42.17.86.26 1.3.26a1 1 0 0 0 .92-.59l2.97-6.6h1.85A2.63 2.63 0 0 0 21 11.2V5.6A2.6 2.6 0 0 0 18.38 3h-2.43a1 1 0 0 0-.03 0 1 1 0 0 0-.03 0H6.77zm-.03 2a1 1 0 0 0 .02 0h8.13v7.59l-2.83 6.28c-.16-.07-.3-.16-.43-.28a1.38 1.38 0 0 1-.41-.99v-3.2a1 1 0 0 0-1-1H5.64a1 1 0 0 0-.01 0 .64.64 0 0 1-.48-.21.6.6 0 0 1-.14-.48l1.11-7.2a.6.6 0 0 1 .21-.36c.11-.1.26-.15.41-.15zm10.21 0h1.43c.17 0 .33.07.44.18.12.11.18.26.18.42v5.6c0 .16-.06.3-.18.42a.63.63 0 0 1-.44.18h-1.43V5z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "video/play":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M20.5 11.1342C21.1667 11.5191 21.1667 12.4813 20.5 12.8662L5.5 21.5265C4.83333 21.9114 4 21.4303 4 20.6605L4 3.33996C4 2.57016 4.83334 2.08903 5.5 2.47394L20.5 11.1342Z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "video/pause":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <rect
                        x="5.5"
                        y="4"
                        width="4"
                        height="16"
                        rx="1"
                        fill="currentColor"
                    />
                    <rect
                        x="14.5"
                        y="4"
                        width="4"
                        height="16"
                        rx="1"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "audio-on":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M13.76 2.93a.99.99 0 0 0-.53.19L4.81 9.07v7l8.5 5.04a1 1 0 0 0 1.5-.87V3.94a1 1 0 0 0-1.05-1zm7.14 2.9a.5.5 0 0 0-.43.69l.05.09.2.32a10 10 0 0 1-.2 10.75.5.5 0 0 0 .83.55 11 11 0 0 0 .23-11.83l-.23-.35-.06-.07a.5.5 0 0 0-.39-.15zm-2.7 1.94a.5.5 0 0 0-.43.69l.04.09.14.2a6.5 6.5 0 0 1-.14 7 .5.5 0 0 0 .84.55 7.5 7.5 0 0 0 .15-8.07l-.15-.24-.07-.08a.5.5 0 0 0-.38-.14zM1.8 9.07a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h2v-7h-2z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "audio-off":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M13.76 2.93a.99.99 0 0 0-.53.19L4.81 9.07v7l8.5 5.04a1 1 0 0 0 1.5-.87V3.94a1 1 0 0 0-1.05-1zm4.16 6.08a.5.5 0 0 0-.27.14.5.5 0 0 0 0 .7L19.79 12l-2.14 2.15a.5.5 0 0 0 0 .7.5.5 0 0 0 .7 0l2.15-2.14 2.15 2.14a.5.5 0 0 0 .7 0 .5.5 0 0 0 0-.7L21.21 12l2.14-2.15a.5.5 0 0 0 0-.7.5.5 0 0 0-.7 0l-2.15 2.14-2.15-2.14a.5.5 0 0 0-.43-.14zm-16.1.06a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h2v-7h-2z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "clock":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M12 1C5.94 1 1 5.94 1 12s4.94 11 11 11 11-4.94 11-11S18.06 1 12 1zm0 2c4.98 0 9 4.02 9 9s-4.02 9-9 9-9-4.02-9-9 4.02-9 9-9zm0 4a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1 1 1 0 0 0-1-1h-4V8a1 1 0 0 0-1-1z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "star":
            return (
                <SvgElement viewBox="0 0 24 24" fill="none" {...props}>
                    <path
                        d="M12.0002 18.0501L17.6135 21.4267C17.9905 21.6531 18.4551 21.3164 18.3559 20.8889L16.871 14.5195L21.8272 10.2361C22.1604 9.94881 21.9817 9.40405 21.5436 9.36612L15.0103 8.80642L12.459 2.80255C12.2872 2.39915 11.7131 2.39915 11.5413 2.80255L8.98887 8.80757L2.4567 9.36612C2.01861 9.40405 1.83992 9.94881 2.1731 10.2361L7.12929 14.5195L5.64324 20.8889C5.54294 21.3164 6.0087 21.6531 6.38569 21.4267L12.0002 18.0501Z"
                        fill="currentColor"
                    />
                </SvgElement>
            );
        case "logo-monogram":
            return (
                <SvgElement viewBox="0 0 87 91" fill="none" {...props}>
                    <path d={logoMonogramPathD} fill="currentColor" />
                </SvgElement>
            );

        default:
            return <LabUISvg name={name} {...props}></LabUISvg>;
    }
};
